// A drag-aware wrapper around the auto-transaction list row. We only render
// the sortable variant of the row when drag is enabled (sort = priorityDesc,
// no filters, not in select mode); otherwise the page renders the plain row.

import type { ReactNode } from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

export type SortableRowProps = {
  id: string;
  children: (handle: ReactNode) => ReactNode;
};

/**
 * Provides a drag handle and applies dnd-kit transforms. The child render-prop
 * receives the drag-handle ReactNode so it can be placed wherever the row
 * design wants — we put it in the avatar slot.
 */
export const SortableRow = ({ id, children }: SortableRowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const handle = (
    <Tooltip title="Drag to reorder">
      <IconButton
        size="small"
        {...attributes}
        {...listeners}
        sx={{ cursor: "grab", touchAction: "none" }}
        aria-label="drag to reorder"
      >
        <DragIndicatorIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  return (
    <Box
      ref={setNodeRef}
      sx={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        bgcolor: isDragging ? "action.hover" : "transparent",
      }}
    >
      {children(handle)}
    </Box>
  );
};
